// src/store/slices/toolModeSlice.ts
/**
 * T-34: Tool Mode State Management
 * 
 * Redux slice for managing the currently active tool mode:
 * - Pan & Zoom (default)
 * - Jump Indicators
 * - Measurement
 * - Annotation/Drawing
 * - Expression Objects
 * 
 * Supports keyboard shortcuts and provides centralized state
 * for tool coordination across the application.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Tool modes available in the application
export type ToolMode = 'pan' | 'jump' | 'measurement' | 'annotation' | 'expression' | 'equation' | 'point' | 'shape' | 'transform' | 'vector';

// Annotation-specific tools that need to be tracked
export type AnnotationTool = 'select' | 'text' | 'equation' | 'pen' | 'highlighter' | 'eraser' | 'rectangle' | 'rectangle-filled' | 'circle' | 'line';

export interface ToolModeState {
  activeTool: ToolMode;
  previousTool: ToolMode | null;
  keyboardShortcutsEnabled: boolean;
  toolHistory: ToolMode[];
  // Track the previous annotation tool for restoration
  previousAnnotationTool: AnnotationTool | null;
}

// Initial state - default to pan mode
const initialState: ToolModeState = {
  activeTool: 'pan',
  previousTool: null,
  keyboardShortcutsEnabled: true,
  toolHistory: ['pan'],
  previousAnnotationTool: null,
};

export const toolModeSlice = createSlice({
  name: 'toolMode',
  initialState,
  reducers: {
    /**
     * Set the active tool mode
     */
    setActiveTool: (state, action: PayloadAction<ToolMode>) => {
      const newTool = action.payload;
      
      // Don't change if already active
      if (state.activeTool === newTool) {
        return;
      }



      // Update state
      state.previousTool = state.activeTool;
      state.activeTool = newTool;
      
      // Add to history (keep last 10 tools)
      state.toolHistory = [newTool, ...state.toolHistory.filter(t => t !== newTool)].slice(0, 10);
    },

    /**
     * Set the previous annotation tool (called when switching away from annotation mode)
     */
    setPreviousAnnotationTool: (state, action: PayloadAction<AnnotationTool>) => {
      state.previousAnnotationTool = action.payload;
    },

    /**
     * Return to the previous tool (useful for ESC key)
     */
    returnToPreviousTool: (state) => {
      if (state.previousTool) {
        const temp = state.activeTool;
        state.activeTool = state.previousTool;
        state.previousTool = temp;
      } else {
        // Default to pan if no previous tool
        state.activeTool = 'pan';
      }
    },

    /**
     * Reset to default tool (pan)
     */
    resetToDefaultTool: (state) => {
      state.previousTool = state.activeTool !== 'pan' ? state.activeTool : null;
      state.activeTool = 'pan';
    },

    /**
     * Enable/disable keyboard shortcuts globally
     */
    setKeyboardShortcutsEnabled: (state, action: PayloadAction<boolean>) => {
      state.keyboardShortcutsEnabled = action.payload;
    },

    /**
     * Clear tool history
     */
    clearToolHistory: (state) => {
      state.toolHistory = [state.activeTool];
    },
  },
});

// Export actions
export const {
  setActiveTool,
  setPreviousAnnotationTool,
  returnToPreviousTool,
  resetToDefaultTool,
  setKeyboardShortcutsEnabled,
  clearToolHistory,
} = toolModeSlice.actions;

// Selectors for accessing tool mode state
export const selectActiveTool = (state: { toolMode: ToolModeState }) => state.toolMode.activeTool;
export const selectPreviousTool = (state: { toolMode: ToolModeState }) => state.toolMode.previousTool;
export const selectKeyboardShortcutsEnabled = (state: { toolMode: ToolModeState }) => state.toolMode.keyboardShortcutsEnabled;
export const selectToolHistory = (state: { toolMode: ToolModeState }) => state.toolMode.toolHistory;
export const selectPreviousAnnotationTool = (state: { toolMode: ToolModeState }) => state.toolMode.previousAnnotationTool;

// Utility selectors
export const selectIsToolActive = (tool: ToolMode) => (state: { toolMode: ToolModeState }) => 
  state.toolMode.activeTool === tool;

export const selectCanReturnToPrevious = (state: { toolMode: ToolModeState }) => 
  state.toolMode.previousTool !== null;

// Tool metadata for UI components
export const TOOL_METADATA = {
  pan: {
    name: 'Pan & Zoom',
    shortcut: 'P',
    description: 'Pan and zoom the number line',
    cursor: 'grab',
  },
  jump: {
    name: 'Jump Indicators',
    shortcut: 'J', 
    description: 'Create jump indicators for operations',
    cursor: 'crosshair',
  },
  measurement: {
    name: 'Measurement',
    shortcut: 'M',
    description: 'Measure distances on the number line',
    cursor: 'crosshair',
  },
  annotation: {
    name: 'Draw & Text',
    shortcut: 'A',
    description: 'Draw annotations and add text',
    cursor: 'crosshair',
  },
  expression: {
    name: 'Expressions',
    shortcut: 'E',
    description: 'Create mathematical expressions',
    cursor: 'crosshair',
  },
  equation: {
    name: 'Graph Equation',
    shortcut: 'G',
    description: 'Graph linear and quadratic equations',
    cursor: 'crosshair',
  },
  point: {
    name: 'Plot Point',
    shortcut: 'T',
    description: 'Plot points on the coordinate grid',
    cursor: 'crosshair',
  },
  shape: {
    name: 'Draw Shape',
    shortcut: 'S',
    description: 'Draw geometric shapes on the coordinate grid',
    cursor: 'crosshair',
  },
  transform: {
    name: 'Transform',
    shortcut: 'R',
    description: 'Apply geometric transformations (translation, reflection, rotation, enlargement)',
    cursor: 'crosshair',
  },
  vector: {
    name: 'Vector',
    shortcut: 'V',
    description: 'Draw vector arrows on the coordinate grid',
    cursor: 'crosshair',
  },
} as const;

export default toolModeSlice.reducer;